import JSZip from "jszip";
import { supabase } from "@/integrations/supabase/client";
import { getAllAncestorFolderIds, getAllDescendantFolderIds, getDocumentById } from "./queries";
import { track } from "@/lib/posthog";
import { fileExtProp } from "@/lib/analytics/eventProps";

export type DownloadDocumentMode = "download" | "blob";

export interface DownloadDocumentOptions {
  /**
   * - "download" (default): triggers a browser download with the provided filename
   * - "blob": returns the downloaded Blob without triggering a download
   */
  mode?: DownloadDocumentMode;
  /** Analytics only — which screen the download was triggered from. */
  surface?: "table" | "viewer" | "version_history" | "signing";
}

/**
 * Download a document with proper filename.
 * Uses blob download to ensure the filename is respected regardless of server headers.
 */
export async function downloadDocument(
  id: string,
  filename: string,
  options: DownloadDocumentOptions = {}
): Promise<Blob> {
  const doc = await getDocumentById(id);
  if (!doc) throw new Error("Document not found");

  // Download as blob to ensure proper filename handling
  const { data, error } = await supabase.storage.from("documents").download(doc.file_path);

  if (error) throw new Error(error.message);
  if (!data) throw new Error("Failed to download file");

  const mode = options.mode ?? "download";
  if (mode === "download") {
    const blobUrl = URL.createObjectURL(data);
    try {
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      URL.revokeObjectURL(blobUrl);
    }
    // Only the "download" mode reaches the user as a file; "blob" is a preview fetch.
    track("document_downloaded", {
      file_ext: fileExtProp(filename),
      surface: options.surface ?? "table",
    });
  }

  return data;
}

function deduplicateFilename(filename: string, usedNames: Set<string>): string {
  let name = filename;
  if (usedNames.has(name.toLowerCase())) {
    const ext = name.lastIndexOf(".");
    const base = ext >= 0 ? name.slice(0, ext) : name;
    const suffix = ext >= 0 ? name.slice(ext) : "";
    let counter = 1;
    do {
      name = `${base} (${counter})${suffix}`;
      counter++;
    } while (usedNames.has(name.toLowerCase()));
  }
  usedNames.add(name.toLowerCase());
  return name;
}

/**
 * Bulk download documents. Single file downloads directly; multiple files are zipped.
 * @param documents - Array of { id, filename, file_path } for each document
 */
export async function bulkDownloadDocuments(
  documents: Array<{ id: string; filename: string; file_path: string }>,
  options: Pick<DownloadDocumentOptions, "surface"> = {}
): Promise<void> {
  if (documents.length === 0) return;

  // Single file: download directly
  if (documents.length === 1) {
    await downloadDocument(documents[0].id, documents[0].filename, options);
    return;
  }

  // Multiple files: zip them
  const zip = new JSZip();
  const usedNames = new Set<string>();
  let successCount = 0;

  for (const doc of documents) {
    try {
      const { data, error } = await supabase.storage.from("documents").download(doc.file_path);
      if (error || !data) continue;

      const name = deduplicateFilename(doc.filename, usedNames);
      zip.file(name, data);
      successCount++;
    } catch {
      // Skip failed downloads, continue with the rest
    }
  }

  if (successCount === 0) {
    throw new Error("No files could be downloaded");
  }

  const zipBlob = await zip.generateAsync({ type: "blob" });
  const zipUrl = URL.createObjectURL(zipBlob);
  try {
    const link = document.createElement("a");
    link.href = zipUrl;
    link.download = `documents-${documents.length}-files.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(zipUrl);
  }
}

export interface FolderRecord {
  id: number;
  name: string;
  parent_id: number | null;
}

interface FolderDocRecord {
  id: number;
  filename: string;
  file_path: string;
  folder_id: number | null;
}

/**
 * Drain a query page by page. Supabase caps a single select at 1000 rows
 * (PostgREST `db-max-rows`), which silently truncates whole-project downloads —
 * every row past the cap would just vanish from the zip.
 */
async function fetchAllPages<T>(
  fetchPage: (
    from: number,
    to: number
  ) => PromiseLike<{ data: T[] | null; error: { message?: string } | null }>,
  errorMessage: string
): Promise<T[]> {
  const PAGE_SIZE = 1000;
  const rows: T[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await fetchPage(from, from + PAGE_SIZE - 1);
    if (error) throw new Error(error.message || errorMessage);
    const page = data ?? [];
    rows.push(...page);
    if (page.length < PAGE_SIZE) return rows;
  }
}

/**
 * Reduce a raw name to a single, safe zip path segment: keep only the leaf after
 * any path separators and reject `.`/`..`. This both keeps a stored name from
 * inventing directories in the zip and neutralizes traversal so no entry can
 * escape its parent on extraction (zip-slip). `fallback` names an empty/dotted
 * segment.
 */
function sanitizePathSegment(raw: string, fallback: string): string {
  const leaf = raw.split(/[/\\]/).pop()?.trim() ?? "";
  if (leaf === "" || leaf === "." || leaf === "..") return fallback;
  return leaf;
}

/**
 * Strip path separators out of a stored filename. Container children (files
 * extracted from .asice/.zip) keep their in-archive path in `filename`
 * (e.g. `PDF/Tiitel.pdf`), and JSZip turns any `/` into a real directory — so
 * an unsanitized name invents folders that don't exist on the platform. Also
 * neutralizes `..` so a filename can never escape its folder in the zip.
 */
export function sanitizeZipEntryName(filename: string): string {
  return sanitizePathSegment(filename, "file");
}

/**
 * Sanitize one folder-name segment before it is joined into a zip path. Folder
 * names are user-controlled (`CreateFolderDialog` only trims), so a name like
 * `../../evil` would otherwise produce a zip entry that escapes the extraction
 * directory — same zip-slip risk `sanitizeZipEntryName` guards for filenames.
 */
function sanitizeFolderSegment(name: string): string {
  return sanitizePathSegment(name, "folder");
}

/**
 * Build a `folder_id -> "Foo/Bar/Baz"` path map rooted at each entry of `rootIds`.
 * Folders outside the subtree of any root get no entry.
 */
export function buildFolderPathMap(
  rootIds: Set<number>,
  folders: Map<number, FolderRecord>
): Map<number, string> {
  const paths = new Map<number, string>();

  const resolve = (id: number): string | null => {
    const cached = paths.get(id);
    if (cached !== undefined) return cached;
    const folder = folders.get(id);
    if (!folder) return null;
    const name = sanitizeFolderSegment(folder.name);
    if (rootIds.has(id)) {
      paths.set(id, name);
      return name;
    }
    if (folder.parent_id === null) return null;
    const parent = resolve(folder.parent_id);
    if (parent === null) return null;
    const path = `${parent}/${name}`;
    paths.set(id, path);
    return path;
  };

  for (const id of folders.keys()) resolve(id);
  return paths;
}

/**
 * Pick the zip *root* folders out of a (cascade-expanded) selection.
 *
 * Selecting a folder in the project view cascades the selection to every
 * descendant folder (see `handleFolderCheckboxToggle` /
 * `fetchFolderSubtree`), so `selectedIds` usually arrives as a whole subtree —
 * not just the folders the user clicked. A folder is a root only when its
 * parent is NOT also selected; rooting `buildFolderPathMap` at these keeps the
 * full nested path for descendants. Treating every selected folder as a root
 * (the previous behaviour) collapsed the hierarchy into a flat set of sibling
 * folders at the zip root — i.e. "folder structure not maintained".
 */
export function deriveSelectionRootIds(
  selectedIds: number[],
  folders: Map<number, FolderRecord>
): Set<number> {
  const selected = new Set<number>(selectedIds);
  const roots = new Set<number>();
  for (const id of selected) {
    const parentId = folders.get(id)?.parent_id ?? null;
    if (parentId === null || !selected.has(parentId)) {
      roots.add(id);
    }
  }
  return roots;
}

/** Ancestor chain for `id`, outermost folder first, ending with `id` itself. */
function ancestorChain(id: number, folders: Map<number, FolderRecord>): number[] {
  const chain: number[] = [];
  const seen = new Set<number>();
  let current: number | null = id;
  while (current !== null && !seen.has(current)) {
    seen.add(current);
    const folder: FolderRecord | undefined = folders.get(current);
    if (!folder) break;
    chain.unshift(current);
    current = folder.parent_id;
  }
  return chain;
}

/**
 * Build a `folder_id -> relative path` map for a loose set of folders, rooted at
 * their deepest common ancestor.
 *
 * Used for documents that are selected on their own rather than via a folder
 * checkbox — most importantly search results, where a search inside "Eelprojekt"
 * surfaces files from its whole subtree as flat rows. Zipping those at the zip
 * root is what made a folder download come out with "all files together, no
 * subfolders". Rooting at the common ancestor reproduces the structure the user
 * sees, without prefixing every entry with the shared parent folders.
 *
 * Folders that all sit in one directory (or a single folder) yield "" — a flat
 * zip, which is the right shape when there is no structure to preserve.
 */
export function buildRelativeFolderPathMap(
  folderIds: number[],
  folders: Map<number, FolderRecord>
): Map<number, string> {
  const paths = new Map<number, string>();
  const unique = [...new Set(folderIds)].filter((id) => folders.has(id));
  if (unique.length === 0) return paths;

  const chains = unique.map((id) => ancestorChain(id, folders));

  // Length of the ancestor prefix every folder shares, which is dropped from the
  // emitted paths. A folder that *is* the shared ancestor maps to "" (its own
  // files sit at the zip root); a lone folder likewise maps to "" — a flat zip.
  let commonLen = 0;
  const shortest = Math.min(...chains.map((c) => c.length));
  while (commonLen < shortest && chains.every((c) => c[commonLen] === chains[0][commonLen])) {
    commonLen++;
  }

  for (let i = 0; i < unique.length; i++) {
    const segments = chains[i]
      .slice(commonLen)
      .map((id) => folders.get(id))
      .filter((f): f is FolderRecord => f !== undefined)
      .map((f) => sanitizeFolderSegment(f.name));
    paths.set(unique[i], segments.join("/"));
  }
  return paths;
}

/**
 * Resolve the owning folder of each standalone document, so it can be zipped at
 * its real path instead of being dumped at the zip root.
 */
async function fetchDocumentFolderIds(docIds: number[]): Promise<Map<number, number | null>> {
  const byDoc = new Map<number, number | null>();
  if (docIds.length === 0) return byDoc;

  const rows = await fetchAllPages<{ id: number; folder_id: number | null }>(
    (from, to) =>
      supabase
        .from("documents")
        .select("id, folder_id")
        .in("id", docIds)
        .order("id")
        .range(from, to),
    "Failed to load document folders"
  );
  for (const row of rows) byDoc.set(row.id, row.folder_id);
  return byDoc;
}

/** Load `document_folders` rows by id, skipping trashed folders. */
async function fetchFolderRecords(ids: number[]): Promise<Map<number, FolderRecord>> {
  const map = new Map<number, FolderRecord>();
  if (ids.length === 0) return map;

  const rows = await fetchAllPages<FolderRecord>(
    (from, to) =>
      supabase
        .from("document_folders")
        .select("id, name, parent_id")
        .in("id", ids)
        .eq("is_deleted", false)
        .order("id")
        .range(from, to),
    "Failed to load folders"
  );
  for (const f of rows) map.set(f.id, f);
  return map;
}

/**
 * Bulk download a mix of standalone documents and folder subtrees as a single
 * zip with folder structure preserved. Single-folder, no-doc selections name
 * the zip after the folder; otherwise the title is `documents-N-files.zip`.
 */
export async function bulkDownloadWithFolders(params: {
  documents: Array<{ id: string; filename: string; file_path: string }>;
  folderIds: string[];
  zipName?: string;
}): Promise<{ successCount: number; failCount: number }> {
  const { documents, folderIds, zipName } = params;

  if (documents.length === 0 && folderIds.length === 0) {
    return { successCount: 0, failCount: 0 };
  }

  const numericRootIds = folderIds.map(Number).filter((id) => Number.isFinite(id));
  const allFolderIds =
    numericRootIds.length > 0 ? await getAllDescendantFolderIds(numericRootIds) : [];

  // Fetch folder metadata for path building. Trashed folders are excluded so
  // they don't materialize as (empty) directories in the zip.
  const folderMap = await fetchFolderRecords(allFolderIds);

  // `numericRootIds` is the cascade-expanded selection (folder + all its
  // descendants), so derive the true top-level roots before building paths —
  // otherwise every folder is rooted at its own name and the nesting collapses.
  const rootIdSet = deriveSelectionRootIds(numericRootIds, folderMap);
  const folderPaths = buildFolderPathMap(rootIdSet, folderMap);

  // Standalone documents keep the structure they have on the platform. A search
  // inside a folder lists its whole subtree as flat rows, so a selection of
  // those rows must not collapse into a single flat zip — resolve each doc's
  // folder and root the paths at the folders' deepest common ancestor.
  const docFolderIds = await fetchDocumentFolderIds(
    documents.map((d) => Number(d.id)).filter((id) => Number.isFinite(id))
  );
  const looseFolderIds = [...new Set(docFolderIds.values())].filter(
    (id): id is number => id !== null && !folderPaths.has(id)
  );
  const looseFolderMap = await fetchFolderRecords(
    looseFolderIds.length > 0 ? await getAllAncestorFolderIds(looseFolderIds) : []
  );
  const loosePaths = buildRelativeFolderPathMap(looseFolderIds, looseFolderMap);

  // Fetch all documents inside these folders. Only the current version of each
  // file is included — older versions are never bundled into a folder download
  // (WHA-3768). Explicit single-version downloads use a separate code path.
  //
  // The filters mirror the document list query (`fetchProjectDocuments`) so the
  // zip contains exactly the files the platform shows: no trashed docs, no
  // hidden docs, no DWG-converted child PDFs, and no children extracted from a
  // container (.asice/.zip) — the container itself downloads as one file.
  const folderDocs =
    allFolderIds.length > 0
      ? await fetchAllPages<FolderDocRecord>(
          (from, to) =>
            supabase
              .from("documents")
              .select("id, filename, file_path, folder_id")
              .in("folder_id", allFolderIds)
              .eq("is_deleted", false)
              .eq("is_latest_version", true)
              .eq("hidden", false)
              .or("is_dwg_child.is.null,is_dwg_child.eq.false")
              .is("parent_document_id", null)
              .order("id")
              .range(from, to),
          "Failed to load folder documents"
        )
      : [];

  const zip = new JSZip();

  // Materialize every folder as an explicit directory entry up front. Zip
  // archives only contain directories that hold at least one entry, so without
  // this, folders whose subtree has no documents silently vanish from the
  // download and the extracted tree no longer mirrors the platform 1:1.
  for (const folderPath of folderPaths.values()) {
    zip.folder(folderPath);
  }
  const usedNamesPerPath = new Map<string, Set<string>>();
  let successCount = 0;
  let failCount = 0;

  // Resolve the zip path of a document's owning folder, or `null` when that
  // folder is trashed. `fetchFolderRecords` drops soft-deleted folders, so a
  // (non-null) folder id absent from BOTH path maps means the folder — or an
  // ancestor — is in the trash. A trashed folder's files are excluded from the
  // export, matching the platform list and the (also-excluded) directory
  // entries; without this they fell back to `""` and spilled loosely into the
  // zip root. `folder_id === null` is a genuine zip-root document.
  const resolveFolderPath = (folderId: number | null): string | null => {
    if (folderId === null) return "";
    // `??` only falls through on undefined, so a real "" path (a common-ancestor
    // or root placement) is kept; a folder missing from both maps yields null.
    return folderPaths.get(folderId) ?? loosePaths.get(folderId) ?? null;
  };

  // Collect the files to zip with their resolved (deterministic) paths first,
  // then download them in bounded-concurrency batches below. Assigning the zip
  // path here — in list order — keeps dedup naming identical regardless of the
  // order the downloads happen to complete.
  const zippedDocIds = new Set<number>();
  const entries: Array<{ filePath: string; filename: string; folderPath: string }> = [];

  // Folder documents — placed under their folder path.
  for (const doc of folderDocs) {
    if (doc.folder_id === null) continue;
    const folderPath = resolveFolderPath(doc.folder_id);
    if (folderPath === null) continue; // owning folder is trashed — exclude
    zippedDocIds.add(doc.id);
    entries.push({ filePath: doc.file_path, filename: doc.filename, folderPath });
  }

  // Standalone documents — placed under their own folder path. A doc that also
  // lives inside one of the selected folders was already zipped above; adding it
  // again would duplicate it (once nested, once loose).
  for (const doc of documents) {
    const numericId = Number(doc.id);
    if (zippedDocIds.has(numericId)) continue;
    const folderPath = resolveFolderPath(docFolderIds.get(numericId) ?? null);
    if (folderPath === null) {
      // The user explicitly picked this document, but its owning folder (or an
      // ancestor) is trashed. Unlike folder-walk docs — where excluding a
      // trashed folder mirrors the platform list — dropping a directly-requested
      // file silently would ship a zip missing a file the user asked for with no
      // indication. Count it as a failure so it surfaces (toast, or the bail
      // below when it was the only thing requested).
      failCount++;
      continue;
    }
    entries.push({ filePath: doc.file_path, filename: doc.filename, folderPath });
  }

  // Download in parallel (bounded), then add to the zip sequentially. Awaiting
  // each download one at a time — as a prior revision did — made a whole-project
  // export (now paginated past the 1000-row cap) fetch every file serially,
  // minutes slower than the batched download it replaced.
  const CONCURRENCY = 5;
  for (let i = 0; i < entries.length; i += CONCURRENCY) {
    const batch = entries.slice(i, i + CONCURRENCY);
    const blobs = await Promise.all(
      batch.map(async (entry) => {
        try {
          const { data, error } = await supabase.storage.from("documents").download(entry.filePath);
          return error ? null : data;
        } catch {
          return null;
        }
      })
    );
    blobs.forEach((data, j) => {
      if (!data) {
        failCount++;
        return;
      }
      const { filename, folderPath } = batch[j];
      if (!usedNamesPerPath.has(folderPath)) {
        usedNamesPerPath.set(folderPath, new Set<string>());
      }
      const used = usedNamesPerPath.get(folderPath)!;
      const unique = deduplicateFilename(sanitizeZipEntryName(filename), used);
      zip.file(folderPath ? `${folderPath}/${unique}` : unique, data);
      successCount++;
    });
  }

  // A selection that resolves to folder structure (even empty folders) is
  // still a valid download — the zip carries the directory tree. Bail only
  // when there is genuinely nothing to deliver. Two distinct empty cases:
  //   - `entries.length > 0`: we queued files to download and every one
  //     failed — a broken download, throw so the user isn't handed an empty
  //     zip of folders that were supposed to hold those files.
  //   - `folderPaths.size === 0`: no folder structure to fall back on either
  //     (e.g. a lone standalone doc whose owning folder is trashed).
  // Keying off `failCount` instead would wrongly discard a valid
  // structure-only zip when an empty folder is combined with a failed
  // standalone doc — the exact case where `entries` is empty but structure
  // exists. Such excluded docs still surface via the `failCount` toast.
  if (successCount === 0 && (entries.length > 0 || folderPaths.size === 0)) {
    throw new Error("No files could be downloaded");
  }

  const zipBlob = await zip.generateAsync({ type: "blob" });
  const zipUrl = URL.createObjectURL(zipBlob);
  try {
    const link = document.createElement("a");
    link.href = zipUrl;
    let downloadName = zipName;
    if (!downloadName) {
      // A single picked folder (its cascade fills `folderIds` with descendants,
      // so key off the derived root set, not `folderIds.length`) names the zip
      // after that folder; everything else uses the file-count title.
      if (rootIdSet.size === 1 && documents.length === 0) {
        const [onlyRootId] = rootIdSet;
        const onlyFolder = folderMap.get(onlyRootId);
        downloadName = `${onlyFolder?.name ?? "folder"}.zip`;
      } else if (successCount === 0) {
        downloadName = "folders.zip";
      } else {
        downloadName = `documents-${successCount}-files.zip`;
      }
    } else if (!downloadName.toLowerCase().endsWith(".zip")) {
      downloadName = `${downloadName}.zip`;
    }
    link.download = downloadName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(zipUrl);
  }

  return { successCount, failCount };
}
